import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'
import { omit as _omit } from 'lodash/fp'

import { Observable, map } from 'rxjs'

import { PaginatedResponse } from '@flowx/core-sdk'
import {
  CreateResourceDataSlicesPayload,
  ResourceDataSlice,
  UpdateResourceDataSlicesPayload,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import {
  CopyTemplateConfigResponse,
  PasteTemplateConfigPayload,
  PasteTemplateConfigResponse,
} from '@flowx/designer/modules/core/dictionary/template-config.dictionary'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

import { ApiHttpProxyService, ForwardProxyMicroservice } from './api-http-proxy.service'
import { DEFAULT_PAGINATION_PARAMS, PaginationQueryParams } from './api-http.service'

import {
  AppResource,
  ApplicationResourceType,
  BuildManifestResourceSummary,
  PROJECT_DATA_TYPE_MAPPING,
  ProxyApiAction,
  mapResource,
} from '../entities/application.entity'
import { UpdateTemplateConfigKey } from '../entities/data-model.entity'
import { ApplicationUiFlowBody, UiFlow } from '../entities/ui-flow.entity'
import { selectUiFlowResourceId } from '../store/selectors/template-config.selectors'

@Injectable({
  providedIn: 'root',
})
export class UiFlowsApiService extends ApiHttpProxyService {
  #store = inject(Store)
  #url = 'api/ui-flow'
  #resourceId = this.#store.selectSignal(selectUiFlowResourceId)
  #processApiPath = inject<string>('PROCESS_API_PATH' as any)

  clearAllCachesUiFlow(uiFlowResourceId: UiFlow['resourceId']): Observable<void> {
    const processApiPath = this.#processApiPath.replace('/', '')
    const url = processApiPath
      ? `${processApiPath}/api/clearAllCachesUiFlow`
      : 'api/clearAllCachesUiFlow'
    return this.httpClient.get<void>(`${this.baseUrl}/${url}`, {
      params: { uiFlowResourceId },
    })
  }

  getAllUiFlows(params: PaginationQueryParams): Observable<AppResource<PaginatedResponse<UiFlow>>> {
    return this.get(
      `${this.#url}/findAll`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.UiFlow,
      },
      {
        params: this.addParams({ ...DEFAULT_PAGINATION_PARAMS, ...params }),
      }
    )
  }

  /**
   * Used by Runtime UI Flow Sessions filtering: returns UI Flow summaries from the latest build,
   * optionally including dependencies.
   *
   * Backend endpoint:
   * /appmanager/api/build-mgmt/app/{applicationId}/rt/ui-flow/summary?includeDependencies=true&latestBuild=true
   */
  getUiFlowSummariesForFiltering(
    includeDependencies = true,
    latestBuild = true
  ): Observable<BuildManifestResourceSummary[]> {
    const { appId } = this.urlParametersStoreService.urlParameters
    return this.httpClient.get<BuildManifestResourceSummary[]>(
      this.getBaseUrl() + `/appmanager/api/build-mgmt/app/${appId}/rt/ui-flow/summary`,
      { params: this.addParams({ includeDependencies, latestBuild }) }
    )
  }

  createUiFlow(payload: ApplicationUiFlowBody): Observable<AppResource<UiFlow>> {
    return this.post(
      this.#url,
      {
        action: ProxyApiAction.CREATE,
        resourceName: payload.name,
        payload,
      },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.UiFlow,
      }
    )
  }

  updateUiFlow(payload: ApplicationUiFlowBody, resourceId: string): Observable<any> {
    return this.patch(this.#url, payload, {
      resourceName: payload.name,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceId,
      action: ProxyApiAction.UPDATE,
    })
  }

  updateUiFlowSettings(
    resourceId: string,
    settings: Required<Pick<UiFlow, 'useInReporting' | 'allowMultipleEditors'>>
  ): Observable<AppResource<UiFlow>> {
    return this.patch(`${this.#url}/settings`, settings, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceId,
      action: ProxyApiAction.PARTIAL_UPDATE,
    })
  }

  deleteUiFlow(resourceId: string): Observable<void> {
    return this.delete(
      this.#url,
      {
        action: ProxyApiAction.DELETE,
        params: { force: true },
      },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.UiFlow,
        resourceId,
      }
    )
  }

  getUiFlowById(resourceId: string): Observable<AppResource<UiFlow>> {
    return this.get(`${this.#url}/getById`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceId,
    })
  }

  getUiFlow(resourceId: string): Observable<AppResource<UiFlow>> {
    return this.get(
      `${this.#url}/templates/getAll`,
      {
        microservice:
          `with-ids-mappings/${ForwardProxyMicroservice.Admin}` as ForwardProxyMicroservice,
        resourceType: ApplicationResourceType.UiFlow,
        resourceId,
      },
      {
        params: this.addParams({
          resourceTypesToIncludeMappingFor: ApplicationResourceType.ReusableTemplate,
        }),
      }
    )
  }

  createNodeTemplate(
    resourceId: number | string,
    template: Partial<TemplateConfigInterface>
  ): Observable<
    AppResource<{ template: TemplateConfigInterface; reordered: Record<number, number> }>
  > {
    return this.patch(`${this.#url}/templates`, template, {
      action: ProxyApiAction.CREATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceId: resourceId as string,
    })
  }

  deleteTemplateConfigs(
    templateConfigs: TemplateConfigInterface[]
  ): Observable<TemplateConfigInterface[]> {
    return this.patch(
      `${this.#url}/templates/bulk`,
      {},
      {
        action: ProxyApiAction.DELETE,
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.UiFlow,
        resourceId: this.#resourceId(),
      },
      {
        params: this.addParams({ ids: templateConfigs.map(({ id }) => id) }),
      }
    )
  }

  bulkUpdateTemplateConfigProperties(
    templateConfigs: TemplateConfigInterface[]
  ): Observable<TemplateConfigInterface[]> {
    const removeChildrenFromTemplateConfig = (
      templateConfig: TemplateConfigInterface
    ): Omit<TemplateConfigInterface, 'templateConfig'> =>
      _omit(['templateConfig', 'meta'], templateConfig)
    const templateConfigsForSave = templateConfigs.map(removeChildrenFromTemplateConfig)
    return this.patch(`${this.#url}/templates/properties/bulk`, templateConfigsForSave, {
      action: ProxyApiAction.PARTIAL_UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceId: this.#resourceId(),
    }).pipe(map((response) => mapResource(response)))
  }

  updateTemplateConfigKey(
    payload: UpdateTemplateConfigKey
  ): Observable<AppResource<Partial<TemplateConfigInterface>>> {
    return this.patch(`${this.#url}/templates/key`, payload, {
      action: ProxyApiAction.PARTIAL_UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceId: this.#resourceId(),
      resourceTypesToIncludeMappingFor: PROJECT_DATA_TYPE_MAPPING,
    })
  }

  bulkUpdateTemplateConfigKey(
    payloads: UpdateTemplateConfigKey[]
  ): Observable<AppResource<Partial<TemplateConfigInterface>>[]> {
    return this.patch(
      `${this.#url}/templates/key/bulk`,
      { keys: payloads },
      {
        action: ProxyApiAction.PARTIAL_UPDATE,
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.UiFlow,
        resourceId: this.#resourceId(),
        // Keys can point at project data-model attributes, so the proxy has to resolve those
        // resource-definition ids to this app's resource ids.
        resourceTypesToIncludeMappingFor: PROJECT_DATA_TYPE_MAPPING,
      }
    )
  }

  copyTemplates(templateConfigsIds: number[]): Observable<CopyTemplateConfigResponse> {
    return this.get(
      `${this.#url}/templates/copy`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.UiFlow,
        resourceId: this.#resourceId(),
      },
      {
        params: this.addParams({ ids: templateConfigsIds }),
      }
    ).pipe(map((response) => mapResource(response)))
  }

  pasteTemplates(
    parentTemplateConfigId: number,
    copiedTemplateConfig: PasteTemplateConfigPayload
  ): Observable<PasteTemplateConfigResponse> {
    return this.patch(
      `${this.#url}/templates/paste/${parentTemplateConfigId}`,
      copiedTemplateConfig,
      {
        action: ProxyApiAction.CREATE,
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.UiFlow,
        resourceId: this.#resourceId(),
        resourceTypesToIncludeMappingFor: [
          ApplicationResourceType.ProjectDataType,
          ApplicationResourceType.ReusableTemplate,
          ApplicationResourceType.CustomComponent,
        ],
      }
    ).pipe(map((response) => mapResource(response) as PasteTemplateConfigResponse))
  }

  // UI Flow Data Slices

  getDataSlices(resourceId: string): Observable<AppResource<ResourceDataSlice[]>> {
    return this.get(`${this.#url}/data-slice`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceId,
    })
  }

  createDataSlice(
    resourceId: string,
    payload: CreateResourceDataSlicesPayload
  ): Observable<AppResource<ResourceDataSlice>> {
    return this.patch(`${this.#url}/data-slice`, payload, {
      action: ProxyApiAction.CREATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
      resourceId,
    })
  }

  updateDataSlice(
    resourceId: string,
    payload: UpdateResourceDataSlicesPayload
  ): Observable<AppResource<ResourceDataSlice>> {
    return this.patch(`${this.#url}/data-slice`, payload, {
      action: ProxyApiAction.UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.UiFlow,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
      resourceId,
    })
  }
}
